import React, { useState, useRef, ChangeEvent, FormEvent } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./FormularioPostulacion.css";
import { useTranslation } from "react-i18next";
import { submitJobApplication } from "../api";
import { JobAppForm } from "../types";
import Notification from "../../../components/ui/Notification";

const MIN_NOMBRE_LENGTH = 2;
const MIN_COMENTARIO_LENGTH = 10;

interface Errors {
  nombre?: string;
  email?: string;
  comentario?: string;
  [key: string]: string | undefined;
}

interface Touched {
  nombre?: boolean;
  email?: boolean;
  comentario?: boolean;
  [key: string]: boolean | undefined;
}


export default function FormularioPostulacion() {
  const { t, i18n } = useTranslation();

  const [form, setForm] = useState<JobAppForm>({
    nombre: "",
    email: "",
    comentario: "",
    cv: null,
  });
  const [phoneValue, setPhoneValue] = useState<string>("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [cvAdjuntado, setCvAdjuntado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [token, setToken] = useState<string | null>(null);
  const captchaRef = useRef<ReCAPTCHA>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Touched>({});
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const isEmailValid = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const countDigits = (value: any) => (value ? value.replace(/\D/g, "").length : 0);

  const phoneError = () => {
    if (!phoneValue || phoneValue.trim() === "") { return t("contacto_form.error_telefono"); }

    if (!isValidPhoneNumber(phoneValue)) { return t("contacto_form.error_telefono_formato"); }
    return "";
  };

  const validateField = (name: string, value: string) => {
    let error = "";
    switch (name) {
      case "nombre":
        if (value.trim().length < MIN_NOMBRE_LENGTH)
          error = t("postulacion.error_nombre");
        break;
      case "email":
        if (!isEmailValid(value)) error = t("postulacion.error_email");
        break;
      case "comentario":
        if (value.trim().length < MIN_COMENTARIO_LENGTH)
          error = t("postulacion.error_comentario");
        break;
      default:
        break;
    }
    setErrors((prev: Errors) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev: Touched) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (e.target instanceof HTMLInputElement && e.target.type === "file") {
      const files = e.target.files;
      if (files && files[0]) {
        setForm({ ...form, cv: files[0] });
        setCvAdjuntado(true);
      } else {
        setForm({ ...form, cv: null });
        setCvAdjuntado(false);
      }
    } else {
      let filteredValue = value;
      if (name === "nombre") {
        filteredValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, "");
      }
      setForm({ ...form, [name]: filteredValue });
      if (touched[name]) {
        validateField(name, filteredValue);
      }
    }
  };

  const handleRemoveFile = () => {
    setForm((prev: JobAppForm) => ({ ...prev, cv: null }));
    setCvAdjuntado(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileName = (name: string | undefined) => {
    if (!name) return "";
    const maxLength = 20;
    if (name.length <= maxLength) return name;

    const extensionIndex = name.lastIndexOf(".");
    if (extensionIndex === -1) return `${name.substring(0, 15)}...`;

    const extension = name.substring(extensionIndex);
    const nameWithoutExt = name.substring(0, extensionIndex);
    return `${nameWithoutExt.substring(0, 5)}...${extension}`;
  };

  const isFormValid = () => {
    const phoneErr = phoneError();
    return (
      form.nombre.trim().length >= MIN_NOMBRE_LENGTH &&
      isEmailValid(form.email) &&
      form.comentario.trim().length >= MIN_COMENTARIO_LENGTH &&
      !phoneErr &&
      cvAdjuntado &&
      (recaptchaSiteKey ? token !== null : true) &&
      Object.values(errors).every((err) => !err)
    );
  };

  const onCaptchaChange = (value: string | null) => {
    setToken(value);
    if (mensaje) setMensaje("");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const currentErrors: Errors = {};
    if (form.nombre.trim().length < MIN_NOMBRE_LENGTH)
      currentErrors.nombre = t("postulacion.error_nombre");
    if (!isEmailValid(form.email))
      currentErrors.email = t("postulacion.error_email");
    if (form.comentario.trim().length < MIN_COMENTARIO_LENGTH)
      currentErrors.comentario = t("postulacion.error_comentario");

    const phoneErr = phoneError();
    if (phoneErr) setPhoneTouched(true);

    if (Object.keys(currentErrors).length > 0 || phoneErr) {
      setErrors(currentErrors);
      setTouched({ nombre: true, email: true, comentario: true });
      return;
    }

    if (recaptchaSiteKey && !token) {
      setMensaje(t("postulacion.captcha_required"));
      return;
    }

    setEnviando(true);
    setMensaje("");

    const formData = new FormData();
    formData.append("name", form.nombre.trim());
    formData.append("email", form.email.trim());
    formData.append("phone", phoneValue || "");
    formData.append("message", form.comentario.trim());
    if (form.cv) formData.append("cv", form.cv);
    if (recaptchaSiteKey) {
      formData.append("recaptchaToken", token || "");
    }

    try {
      await submitJobApplication(formData);
      setSuccessMessage(t("postulacion.exito"));
      setEnviado(true);
      setForm({
        nombre: "",
        email: "",
        comentario: "",
        cv: null,
      });
      setPhoneValue("");
      setPhoneTouched(false);
      setTouched({});
      setErrors({});
      setCvAdjuntado(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error: any) {
      console.error("error " + error);
      const baseMessage = error.friendlyMessage || t("postulacion.error_connection");
      setMensaje(baseMessage);
      if (captchaRef.current) captchaRef.current.reset();
      setToken(null);
    }
    setEnviando(false);
  };

  const currentPhoneError = phoneTouched ? phoneError() : "";

  return (
    <div className="postulate-form-section">
      <Notification 
        message={successMessage} 
        type="success" 
        onClose={() => setSuccessMessage(null)} 
      />
      <div className="form-info">
        <h3>{t("postulacion.titulo")}</h3>
        <p>{t("postulacion.subtitulo")}</p>
      </div>

      <form className="form-fields" onSubmit={handleSubmit}>
        <div className="input-group">
          <label htmlFor="nombre" className="sr-only">
            {t("postulacion.placeholder_nombre")}
          </label>
          <input
            id="nombre"
            type="text"
            className={touched.nombre && errors.nombre ? "invalid" : ""}
            placeholder={t("postulacion.placeholder_nombre")}
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={100}
            required
            disabled={enviando || enviado}
          />
          {touched.nombre && errors.nombre && (
            <p className="error-message">{errors.nombre}</p>
          )}
        </div>

        <div className="input-group">
          <label htmlFor="email" className="sr-only">
            {t("postulacion.placeholder_email")}
          </label>
          <input
            id="email"
            type="email"
            className={touched.email && errors.email ? "invalid" : ""}
            placeholder={t("postulacion.placeholder_email")}
            name="email"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={100}
            required
            disabled={enviando || enviado}
          />
          {touched.email && errors.email && (
            <p className="error-message">{errors.email}</p>
          )}
        </div>

        <div className="input-group">
          <label className="sr-only">
            {t("postulacion.placeholder_telefono")}
          </label>
          <div
            className={`phone-field-wrapper${currentPhoneError ? " phone-invalid" : ""}`}
          >
            <PhoneInput
              international
              defaultCountry="AR"
              value={phoneValue}
              onChange={(val) => setPhoneValue(val || "")}
              onBlur={() => setPhoneTouched(true)}
              disabled={enviando || enviado}
              placeholder={t("postulacion.placeholder_telefono")}
              maxLength="20"
            />
          </div>
          {currentPhoneError && (
            <p className="error-message">{currentPhoneError}</p>
          )}
        </div>

        <div className="input-group">
          <label htmlFor="comentario" className="sr-only">
            {t("postulacion.placeholder_comentario")}
          </label>
          <textarea
            id="comentario"
            className={touched.comentario && errors.comentario ? "invalid" : ""}
            placeholder={t("postulacion.placeholder_comentario")}
            name="comentario"
            value={form.comentario}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={1000}
            rows={3}
            required
            disabled={enviando || enviado}
          />
          {touched.comentario && errors.comentario && (
            <p className="error-message">{errors.comentario}</p>
          )}
        </div>

        {!enviado && (
          <div
            className="recaptcha-wrapper"
            style={{
              marginBottom: "15px", 
            } as React.CSSProperties}
          >
            {recaptchaSiteKey ? (
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={recaptchaSiteKey}
                hl={i18n.language}
                key={i18n.language}
                onChange={onCaptchaChange}
              />
            ) : (
              <p className="error-message" style={{ margin: 0 }}>
                {t("postulacion.captcha_not_configured")}
              </p>
            )}
          </div>
        )}

        <div className="form-actions-row">
          <label
            className={`cv-label postula-btn-outline ${enviando || enviado ? "disabled" : ""}`}
            style={{
              display: "block",
              boxSizing: "border-box", // Evita que sobresalga
              maxWidth: "100%", // Obliga a ajustarse al contenedor
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: enviando || enviado ? "not-allowed" : "pointer",
            }}
          >
            {t("postulacion.adjuntar_cv")}
            <input
              ref={fileInputRef}
              type="file"
              name="cv"
              accept=".pdf,.doc,.docx,.txt,.html"
              onChange={handleChange}
              required
              className="sr-only"
              disabled={enviando || enviado}
            />
          </label>

          <button
            type="submit"
            className="postulate-btn"
            disabled={!isFormValid() || enviando || enviado}
            style={{
              display: "block",
              boxSizing: "border-box",
              maxWidth: "100%",
              opacity: !isFormValid() || enviando || enviado ? 0.6 : 1,
              cursor:
                !isFormValid() || enviando || enviado
                  ? "not-allowed"
                  : "pointer",
            } as React.CSSProperties}
          >
            {enviando
              ? t("postulacion.enviando")
              : enviado
                ? t("postulacion.enviado")
                : t("postulacion.boton")}
          </button>
        </div>

        {cvAdjuntado && !enviado && (
          <div
            className="cv-adjuntado"
            aria-live="polite"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "15px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <img
                src="/freepik__background__7433.webp"
                alt="CV"
                style={{ width: "1.2rem", height: "1.2rem" }}
              />
              {t("postulacion.cv_adjunto")}:{" "}
              <strong>{formatFileName(form.cv?.name || "")}</strong>
            </span>
            <button
              type="button"
              onClick={handleRemoveFile}
              disabled={enviando}
              aria-label="Eliminar archivo"
              style={{
                background: "transparent",
                border: "none",
                color: "#ff4d4f",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "1.5rem",
                padding: "0 5px",
                lineHeight: "1",
                display: "flex",
                alignItems: "center",
              }}
            >
              ×
            </button>
          </div>
        )}

        {mensaje && (
          <div
            className={`mensaje-form-premium ${enviado ? "success" : "error"}`}
            role="status"
            aria-live="polite"
          >
            {!enviado && (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            )}
            {enviado && (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            )}
            <span>{mensaje}</span>
          </div>
        )}
      </form>
    </div>
  );
}
